/**
 * Environment effects panel — G34 operator entry point.
 *
 * Lists all 12 synthetic environment layers with visibility toggles and
 * settings shortcuts. Mounted in the left dock when Scene Tree V2 is active
 * (environment layers are intentionally absent from the tree).
 */

import {
  useCallback,
  useMemo,
  useState,
  type CSSProperties,
} from 'react';
import type { Layer } from '@geosoft/viewer-sdk';
import { Icon, tokens } from '@/design';
import {
  dispatchToolCardRequest,
  selectEnvironmentLayer,
} from '@/app/sceneSelection';
import { useViewerSlice } from '@/state/useViewerSlice';
import { useViewerStore } from '@/state/useViewerStore';
import type { EnvironmentKind, EnvironmentPresetId } from '../catalog';
import { DEFAULT_ENVIRONMENT_LAYER_IDS } from '../catalog';
import { EnvironmentPresetMenu } from './EnvironmentPresetMenu';
import {
  ENVIRONMENT_KIND_ICON,
  ENVIRONMENT_KIND_LABEL,
} from './EnvironmentToolCard';

const ENVIRONMENT_KIND_ORDER: readonly EnvironmentKind[] = [
  'sky',
  'sun',
  'moon',
  'stars',
  'fog',
  'clouds',
  'wind',
  'rain',
  'snow',
  'lightning',
  'darkmode',
  'time-of-day',
];

export interface EnvironmentEffectsPanelProps {
  readonly layers: readonly Layer[];
  readonly visibleIds: ReadonlySet<string>;
  readonly onSetVisible: (layerId: string, visible: boolean) => void;
  readonly onSetManyVisible?: (layerIds: readonly string[], visible: boolean) => void;
}

function readEnvironmentKind(layer: Layer): EnvironmentKind | null {
  const kind = (layer.style as Record<string, unknown> | undefined)?.environmentKind;
  return typeof kind === 'string' ? (kind as EnvironmentKind) : null;
}

function isEnvironmentLayer(layer: Layer): boolean {
  return layer.kind === 'environment' || layer.id.startsWith('layer:environment:');
}

export function EnvironmentEffectsPanel({
  layers,
  visibleIds,
  onSetVisible,
  onSetManyVisible,
}: EnvironmentEffectsPanelProps) {
  const [expanded, setExpanded] = useState(true);
  const masterEnabled = useViewerSlice((s) => s.environmentMasterEnabled);
  const activePreset = useViewerSlice((s) => s.environmentPreset);
  const setMasterEnabled = useViewerStore((s) => s.setEnvironmentMasterEnabled);
  const setEnvironmentPreset = useViewerStore((s) => s.setEnvironmentPreset);
  const setSceneSelection = useViewerStore((s) => s.setSceneSelection);

  const environmentLayers = useMemo(() => {
    const byKind = new Map<EnvironmentKind, Layer>();
    for (const layer of layers) {
      if (!isEnvironmentLayer(layer)) continue;
      const kind = readEnvironmentKind(layer);
      if (kind) byKind.set(kind, layer);
    }
    return ENVIRONMENT_KIND_ORDER
      .map((kind) => byKind.get(kind))
      .filter((layer): layer is Layer => layer != null);
  }, [layers]);

  const environmentLayerIds = useMemo(
    () => environmentLayers.map((layer) => layer.id),
    [environmentLayers],
  );

  const visibleCount = useMemo(
    () => environmentLayers.filter((layer) => visibleIds.has(layer.id)).length,
    [environmentLayers, visibleIds],
  );

  const openEffectSettings = useCallback(
    (layer: Layer) => {
      const selection = selectEnvironmentLayer(layer.id, { source: 'tool-card' });
      setSceneSelection(selection);
      dispatchToolCardRequest(selection);
    },
    [setSceneSelection],
  );

  const handleMasterToggle = useCallback(() => {
    const next = !masterEnabled;
    setMasterEnabled(next);
    if (!next && onSetManyVisible) {
      onSetManyVisible(environmentLayerIds, false);
    }
  }, [environmentLayerIds, masterEnabled, onSetManyVisible, setMasterEnabled]);

  const handleSelectPreset = useCallback(
    (preset: EnvironmentPresetId) => {
      setEnvironmentPreset(preset);
    },
    [setEnvironmentPreset],
  );

  const handleResetPreset = useCallback(() => {
    setEnvironmentPreset('clear');
  }, [setEnvironmentPreset]);

  if (environmentLayers.length === 0) return null;

  const sectionStyle: CSSProperties = {
    borderTop: tokens.border.thin,
    background: tokens.color.surface[0],
  };

  const headerStyle: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.space[2],
    padding: `${tokens.space[2]}px ${tokens.space[3]}px`,
    minHeight: tokens.density.touchMin,
  };

  const chipBtnStyle = (): CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.space[1],
    minHeight: tokens.density.touchMin,
    minWidth: tokens.density.touchMin,
    padding: `${tokens.space[1]}px ${tokens.space[2]}px`,
    border: tokens.border.thin,
    borderRadius: tokens.radius.sm,
    background: tokens.color.surface[1],
    color: tokens.color.ink.secondary,
    cursor: 'pointer',
    fontSize: tokens.font.size.xs,
  });

  return (
    <section
      data-testid="environment-effects-panel"
      aria-label="Environment effects"
      style={sectionStyle}
    >
      <div style={headerStyle}>
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          aria-expanded={expanded}
          aria-controls="environment-effects-list"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: tokens.space[2],
            flex: 1,
            border: 0,
            background: 'transparent',
            color: tokens.color.ink.primary,
            cursor: 'pointer',
            textAlign: 'left',
            minHeight: tokens.density.touchMin,
          }}
        >
          <Icon
            name={expanded ? 'keyboard_arrow_up' : 'chevron_right'}
            size={16}
            decorative
          />
          <span style={{ flex: 1, fontWeight: tokens.font.weight.medium }}>
            Environment
          </span>
          <span
            style={{
              color: tokens.color.ink.secondary,
              fontSize: tokens.font.size.xs,
            }}
          >
            {visibleCount}/{environmentLayers.length}
          </span>
        </button>
        {onSetManyVisible ? (
          <>
            <button
              type="button"
              onClick={() => onSetManyVisible(environmentLayerIds, true)}
              aria-label="Show all environment effects"
              style={chipBtnStyle()}
            >
              Show all
            </button>
            {visibleCount > 0 ? (
              <button
                type="button"
                onClick={() => onSetManyVisible(environmentLayerIds, false)}
                aria-label="Hide all environment effects"
                style={chipBtnStyle()}
              >
                Hide all
              </button>
            ) : null}
          </>
        ) : null}
      </div>

      {expanded ? (
        <div
          id="environment-effects-list"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: tokens.space[2],
            padding: `0 ${tokens.space[3]}px ${tokens.space[3]}px`,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: tokens.space[2],
            }}
          >
            <button
              type="button"
              role="switch"
              aria-checked={masterEnabled}
              aria-label="Use environment effects"
              data-testid="environment-master-toggle"
              onClick={handleMasterToggle}
              style={{
                ...chipBtnStyle(),
                color: masterEnabled
                  ? tokens.color.accent.primary
                  : tokens.color.ink.secondary,
              }}
            >
              <Icon name={masterEnabled ? 'visibility' : 'visibility_off'} size={18 as any} decorative />
              {masterEnabled ? 'Effects on' : 'Effects off'}
            </button>
            <EnvironmentPresetMenu
              activePreset={activePreset}
              onSelectPreset={handleSelectPreset}
              onReset={handleResetPreset}
            />
          </div>

          {environmentLayers.map((layer) => {
            const kind = readEnvironmentKind(layer) ?? 'sky';
            const visible = visibleIds.has(layer.id);
            const label = ENVIRONMENT_KIND_LABEL[kind];
            const icon = ENVIRONMENT_KIND_ICON[kind];
            const toggleDisabled = !masterEnabled;

            return (
              <div
                key={layer.id}
                data-testid={`environment-effect-${kind}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto auto',
                  alignItems: 'center',
                  gap: tokens.space[2],
                  padding: tokens.space[2],
                  border: tokens.border.thin,
                  borderRadius: tokens.radius.md,
                  background: tokens.color.surface[1],
                  opacity: toggleDisabled ? 0.55 : 1,
                }}
              >
                <Icon name={icon} size={18 as any} decorative />
                <button
                  type="button"
                  onClick={() => openEffectSettings(layer)}
                  aria-label={`Open settings for ${label}`}
                  style={{
                    padding: 0,
                    border: 'none',
                    background: 'transparent',
                    color: tokens.color.ink.primary,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontSize: tokens.font.size.sm,
                    fontWeight: tokens.font.weight.medium,
                  }}
                >
                  {label}
                </button>
                <button
                  type="button"
                  onClick={() => onSetVisible(layer.id, !visible)}
                  disabled={toggleDisabled}
                  aria-label={visible ? `Hide ${label}` : `Show ${label}`}
                  data-testid={`environment-effect-toggle-${kind}`}
                  style={chipBtnStyle()}
                >
                  <Icon
                    name={visible ? 'visibility' : 'visibility_off'}
                    size={16}
                    decorative
                  />
                </button>
                <button
                  type="button"
                  onClick={() => openEffectSettings(layer)}
                  aria-label={`Adjust ${label} parameters`}
                  data-testid={`environment-effect-settings-${kind}`}
                  style={chipBtnStyle()}
                >
                  <Icon name="tune" size={16} decorative />
                </button>
              </div>
            );
          })}
        </div>
      ) : null}
    </section>
  );
}

/** Stable layer ids for tests and diagnostics. */
export const ENVIRONMENT_EFFECT_LAYER_IDS = DEFAULT_ENVIRONMENT_LAYER_IDS;
